import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  CreditCard, DollarSign, Calendar, CheckCircle2, Clock,
  AlertCircle, Loader2, ChevronRight,
} from 'lucide-react';
import { paymentService } from '../services/paymentService';
import { tenantService } from '../services/tenantService';
import BankSetupForm, { ConnectedBankCard } from './BankSetupForm';

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

const statusColors = {
  Paid:          'bg-green-100 text-green-800',
  Processing:    'bg-blue-100 text-blue-800',
  Issued:        'bg-gray-100 text-gray-800',
  Overdue:       'bg-red-100 text-red-800',
  Failed:        'bg-red-100 text-red-800',
  'Partially Paid': 'bg-amber-100 text-amber-800',
  Void:          'bg-gray-100 text-gray-500',
};

const InvoiceStatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
    {status}
  </span>
);

// ── State A: bank not connected ───────────────────────────────────────────────
const TenantBankSetup = ({ tenantProfile, lease, onComplete }) => (
  <div className="max-w-md mx-auto py-8 px-4 space-y-6">
    <div className="text-center">
      <div className="w-14 h-14 bg-cyan-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <CreditCard className="h-7 w-7 text-cyan-600" />
      </div>
      <h2 className="text-xl font-semibold mb-1">Connect Your Bank Account</h2>
      <p className="text-sm text-muted-foreground">
        Add your bank account to enable automatic ACH rent payments.
      </p>
    </div>

    {lease && (
      <Card>
        <CardContent className="pt-4 pb-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Property</span>
            <span className="font-medium">{tenantProfile?.property?.name || '—'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Monthly Rent</span>
            <span className="font-semibold text-cyan-700">${lease.monthlyRent?.toLocaleString()}</span>
          </div>
        </CardContent>
      </Card>
    )}

    <BankSetupForm
      initialName={tenantProfile?.name || ''}
      onSave={async (data) => {
        const res = await paymentService.setupBankAccount(data);
        return res;
      }}
      onSuccess={onComplete}
    />
  </div>
);


const PAYABLE = ['Issued', 'Overdue', 'Partially Paid'];

const PENDING_STATUSES = ['Issued', 'Overdue', 'Partially Paid', 'Processing'];
const PAID_STATUSES    = ['Paid', 'Void'];

// ── State C: active subscription ─────────────────────────────────────────────
const ActivePayments = ({ achStatus, ledger, invoices, onChangeBankAccount, onPayInvoice }) => {
  const [selected, setSelected]         = useState(null);
  const [paying, setPaying]             = useState(false);
  const [invoiceFilter, setInvoiceFilter] = useState('Pending'); // 'Pending' | 'Paid' | 'All'

  const outstanding = invoices
    .filter(inv => ['Issued', 'Partially Paid', 'Processing', 'Overdue'].includes(inv.status))
    .reduce((s, inv) => s + (inv.totalAmount - (inv.paidAmount || 0)), 0);

  const lastPaid = invoices.find(inv => inv.status === 'Paid');
  const nextDue  = invoices.find(inv => ['Issued', 'Processing'].includes(inv.status));

  const filteredInvoices = invoices.filter(inv => {
    if (invoiceFilter === 'Pending') return PENDING_STATUSES.includes(inv.status);
    if (invoiceFilter === 'Paid')    return PAID_STATUSES.includes(inv.status);
    return true;
  });

  const handlePay = async () => {
    setPaying(true);
    try {
      await onPayInvoice(selected._id);
      setSelected(prev => prev ? { ...prev, status: 'Processing' } : null);
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Outstanding</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
              ${outstanding.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Next Payment</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {nextDue ? (
              <>
                <div className="text-2xl font-bold">${nextDue.totalAmount?.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  {nextDue.dueDate ? new Date(nextDue.dueDate).toLocaleDateString() : 'Processing'}
                </p>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">No upcoming payments</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Payment Method</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="pt-3 pb-3">
            <ConnectedBankCard
              bankName={achStatus.bankName}
              last4={achStatus.bankLast4}
              status="verified"
              onUpdate={onChangeBankAccount}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Payment</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {lastPaid ? (
              <>
                <div className="text-2xl font-bold">${lastPaid.paidAmount?.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  {lastPaid.updatedAt ? new Date(lastPaid.updatedAt).toLocaleDateString() : ''}
                </p>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">No payments yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoice split panel */}
      <div className="flex gap-0 border rounded-lg overflow-hidden" style={{ height: 'calc(100vh - 24rem)' }}>
        {/* Invoice list */}
        <div className={`border-r overflow-y-auto flex-shrink-0 ${selected ? 'w-72' : 'flex-1'}`}>
          <div className="p-3 border-b bg-gray-50 flex gap-1">
            {['Pending', 'Paid', 'All'].map(f => (
              <button
                key={f}
                onClick={() => { setInvoiceFilter(f); setSelected(null); }}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${invoiceFilter === f ? 'bg-cyan-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`}
              >
                {f}
              </button>
            ))}
          </div>
          {filteredInvoices.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No {invoiceFilter.toLowerCase()} invoices.
            </div>
          ) : (
            filteredInvoices.map(inv => (
              <div
                key={inv._id}
                role="button"
                onClick={() => setSelected(selected?._id === inv._id ? null : inv)}
                className={`flex items-center justify-between px-4 py-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${selected?._id === inv._id ? 'bg-cyan-50 border-l-2 border-l-cyan-500' : ''}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{inv.invoiceNumber || 'Invoice'}</div>
                  <div className="text-xs text-muted-foreground">
                    {inv.issuedAt ? new Date(inv.issuedAt).toLocaleDateString() : ''}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 ml-3 flex-shrink-0">
                  <span className="text-sm font-semibold">${(inv.totalAmount || 0).toLocaleString()}</span>
                  <InvoiceStatusBadge status={inv.status} />
                </div>
              </div>
            ))
          )}
        </div>

        {/* Invoice detail */}
        {selected && (
          <div className="flex-1 overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">{selected.invoiceNumber}</h3>
              <InvoiceStatusBadge status={selected.status} />
            </div>
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Issued {selected.issuedAt ? new Date(selected.issuedAt).toLocaleDateString() : '—'}
                {selected.dueDate && ` · Due ${new Date(selected.dueDate).toLocaleDateString()}`}
              </div>
              {selected.lineItems?.length > 0 && (
                <table className="w-full text-sm border rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Description</th>
                      <th className="text-right px-4 py-2 font-medium text-gray-600">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.lineItems.map((item, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-4 py-2 text-gray-700">{item.description}</td>
                        <td className="px-4 py-2 text-right font-medium">${(item.amount || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                    <tr className="border-t bg-gray-50 font-semibold">
                      <td className="px-4 py-2">Total</td>
                      <td className="px-4 py-2 text-right">${(selected.totalAmount || 0).toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              )}
              {PAYABLE.includes(selected.status) && (
                <div className="pt-2 border-t">
                  <Button
                    className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                    onClick={handlePay}
                    disabled={paying}
                  >
                    {paying
                      ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Submitting…</>
                      : `Pay $${(selected.totalAmount - (selected.paidAmount || 0)).toLocaleString()} Now`
                    }
                  </Button>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    ACH debit from ••••{achStatus?.bankLast4 || '****'} · settles in 3–5 business days
                  </p>
                </div>
              )}
              {selected.status === 'Processing' && (
                <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  <Clock className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  ACH payments typically take 3–5 business days to settle.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Payments component ───────────────────────────────────────────────────
const Payments = ({ user }) => {
  const [loading, setLoading]       = useState(true);
  const [achStatus, setAchStatus]   = useState(null);
  const [invoices, setInvoices]     = useState([]);
  const [ledger, setLedger]         = useState(null);
  const [tenantProfile, setTenantProfile] = useState(null);
  const [error, setError]           = useState('');
  const [changingBank, setChangingBank] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [statusRes, invoicesRes, profileRes] = await Promise.allSettled([
        paymentService.getMyAchStatus(),
        paymentService.getMyInvoices(),
        tenantService.getMe(),
      ]);
      if (statusRes.status === 'fulfilled')  setAchStatus(statusRes.value);
      if (invoicesRes.status === 'fulfilled') setInvoices(invoicesRes.value);
      if (profileRes.status === 'fulfilled') setTenantProfile(profileRes.value);
    } catch (err) {
      setError('Failed to load payment data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-muted-foreground">{error}</p>
          <Button variant="outline" className="mt-4" onClick={loadData}>Retry</Button>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    const status = achStatus?.achStatus || 'not_started';

    // Change bank account flow
    if (changingBank) {
      return (
        <TenantBankSetup
          tenantProfile={tenantProfile}
          lease={lease}
          onComplete={() => { setChangingBank(false); loadData(); }}
        />
      );
    }

    // Not connected yet
    if (status === 'not_started') {
      return (
        <TenantBankSetup
          tenantProfile={tenantProfile}
          lease={lease}
          onComplete={loadData}
        />
      );
    }

    // Connected, waiting for landlord to activate
    if (status === 'verified' && !achStatus?.subscriptionActive) {
      return (
        <div className="max-w-md mx-auto py-10 px-4 space-y-6">
          <div className="text-center">
            <Clock className="h-14 w-14 text-amber-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Bank Connected</h3>
            <p className="text-sm text-muted-foreground">
              Your bank has been connected. Your landlord will activate recurring payments
              {lease?.monthlyRent ? ` of $${lease.monthlyRent.toLocaleString()}/mo` : ''}.
            </p>
          </div>
          {achStatus?.bankName && (
            <ConnectedBankCard
              bankName={achStatus.bankName}
              last4={achStatus.bankLast4}
              status="verified"
              onUpdate={() => setChangingBank(true)}
            />
          )}
        </div>
      );
    }

    // Pending micro-deposit verification
    if (status === 'pending') {
      return (
        <div className="max-w-md mx-auto py-10 px-4 text-center space-y-4">
          <Clock className="h-14 w-14 text-amber-400 mx-auto" />
          <h3 className="text-lg font-semibold">Verification Pending</h3>
          <p className="text-sm text-muted-foreground">
            Stripe has sent two small deposits to your account. Once they appear (1–2 business days),
            verify the amounts to activate your bank account.
          </p>
        </div>
      );
    }

    const handlePayInvoice = async (invoiceId) => {
      await paymentService.payInvoice(invoiceId);
      loadData();
    };

    return (
      <ActivePayments
        achStatus={achStatus}
        ledger={ledger}
        invoices={invoices}
        onChangeBankAccount={() => setChangingBank(true)}
        onPayInvoice={handlePayInvoice}
      />
    );
  };

  const lease = tenantProfile?.lease;
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <CreditCard className="h-6 w-6 text-cyan-600" />
        <h2 className="text-3xl font-bold tracking-tight">NivaPay</h2>
      </div>

      {/* Lease summary */}
      {lease && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-gray-700">Your Lease</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Property</p>
                <p className="font-medium">{lease.property?.name || tenantProfile?.property?.name || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Unit</p>
                <p className="font-medium">{lease.unitNumber || tenantProfile?.unitNumber || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Monthly Rent</p>
                <p className="font-semibold text-gray-900">${lease.monthlyRent?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Lease Term</p>
                <p className="font-medium">{fmtDate(lease.leaseStart)} – {fmtDate(lease.leaseEnd)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {renderContent()}
    </div>
  );
};

export default Payments;
